import { z } from 'zod';
import { RecordIdSchema } from './identifiers';
import { DecimalStringSchema, MoneySchema } from './money';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Deliveries and logistics (WH-DELIVERY-001, 2026-10-17).
 *
 * A delivery is the **logistics** of moving something the stores or procurement already recorded: a
 * stock transfer, an issue, an outgoing custody, or a purchase order coming in from a supplier. It
 * carries the vehicle, driver, schedule, dispatch and proof of delivery. It is **not a second stock
 * ledger**: no delivery state moves a quantity. Stock moves only through the stock documents (a
 * transfer enters the receiving store when its keeper acknowledges it — in part, if part arrived; a
 * purchase enters through the goods receipt), so:
 *
 * - a transfer's delivery is completed only for what its keeper acknowledged, and never for more;
 * - damaged or rejected quantities recorded on a delivery never become usable stock — they stay
 *   outstanding on the source (in transit for a transfer) until replaced, received later or returned;
 * - a delivery can carry part of its source; what is still outstanding is planned on a follow-up;
 * - cancelling a dispatched delivery records that the goods turned back — it restores nothing: the
 *   source document (cancel the transfer, record a return) says where the stock is;
 * - freight and other delivery costs are recorded once, with how the ledger hears of them: on the
 *   supplier's invoice (already in the order), as a company expense (linked to that expense record,
 *   which posts it), or not charged. A delivery never posts.
 */

export const DELIVERY_STATES = [
  'planned',
  'dispatched',
  'delivered',
  'partiallyDelivered',
  'failed',
  'cancelled',
] as const;
export const DeliveryStateSchema = z.enum(DELIVERY_STATES);
export type DeliveryState = z.infer<typeof DeliveryStateSchema>;

/** Planned or on the road: its quantities are still expected. */
export const DELIVERY_IN_FLIGHT_STATES: readonly DeliveryState[] = ['planned', 'dispatched'];
/** Handed over (fully or with exceptions): its accepted quantities count as delivered. */
export const DELIVERY_COMPLETED_STATES: readonly DeliveryState[] = [
  'delivered',
  'partiallyDelivered',
];

export const DELIVERY_SOURCES = [
  'stockTransfer',
  'stockIssue',
  'custody',
  'purchaseOrder',
] as const;
export const DeliverySourceTypeSchema = z.enum(DELIVERY_SOURCES);
export type DeliverySourceType = z.infer<typeof DeliverySourceTypeSchema>;

export const DELIVERY_ACTIONS = ['dispatch', 'deliver', 'fail', 'replan', 'cancel'] as const;

export const DRIVER_KINDS = ['employee', 'external'] as const;

/** Delivery costs: what they are, and how the ledger hears of them (never through the delivery). */
export const DELIVERY_COST_KINDS = ['freight', 'loading', 'unloading', 'other'] as const;
export const DELIVERY_COST_TREATMENTS = [
  /** Charged by the supplier on its order and invoice — recorded here for information only. */
  'onSupplierInvoice',
  /** Paid by the company to a carrier: the linked expense record carries and posts it. */
  'companyExpense',
  /** Borne by the supplier or the carrier, or free. */
  'notCharged',
] as const;

/** What a delivery return links to: the stock document or goods receipt that records the goods. */
export const DELIVERY_RETURN_DOCUMENTS = ['stockDocument', 'goodsReceipt'] as const;

const DriverSchema = z.strictObject({
  kind: z.enum(DRIVER_KINDS),
  accountId: z.string().min(1).max(200).optional(),
  name: z.string().trim().min(2).max(160),
  phone: z.string().trim().min(5).max(30).optional(),
});

const VehicleSchema = z.strictObject({
  plate: z.string().trim().min(1).max(30),
  description: z.string().trim().min(1).max(120).optional(),
});

export const DeliveryLineSchema = z.strictObject({
  lineNo: z.number().int().positive(),
  /** The source document's line this carries (its line number; custody lines in order). */
  sourceLineNo: z.number().int().positive(),
  itemId: z.string().optional(),
  description: z.string(),
  /** Planned on this delivery. */
  quantity: DecimalStringSchema,
  unit: z.string(),
  /** Once handed over: accepted by the receiver, damaged, refused; `short` = never handed over. */
  accepted: DecimalStringSchema.optional(),
  damaged: DecimalStringSchema.optional(),
  rejected: DecimalStringSchema.optional(),
  short: DecimalStringSchema.optional(),
  /** Accepted quantity later sent back (see `returns`). */
  returned: DecimalStringSchema,
});
export type DeliveryLine = z.infer<typeof DeliveryLineSchema>;

export const DeliveryCostSchema = z.strictObject({
  costId: RecordIdSchema,
  kind: z.enum(DELIVERY_COST_KINDS),
  amount: MoneySchema,
  treatment: z.enum(DELIVERY_COST_TREATMENTS),
  expense: z.strictObject({ expenseId: RecordIdSchema, number: z.string() }).optional(),
  /** `onSupplierInvoice`: the purchase order charge line this cost is an allocation of. */
  supplierCharge: z
    .strictObject({ orderId: RecordIdSchema, lineNo: z.number().int().positive() })
    .optional(),
  /** A separate charge's own reference (normalised). */
  reference: z.string().optional(),
  note: z.string().optional(),
  recordedByAccountId: z.string(),
  recordedAt: InstantSchema,
  /** Withdrawn in error: shown, no longer counted. */
  withdrawn: z
    .strictObject({ reason: z.string(), at: InstantSchema, byAccountId: z.string() })
    .optional(),
});
export type DeliveryCost = z.infer<typeof DeliveryCostSchema>;

export const DeliveryReturnSchema = z.strictObject({
  returnId: RecordIdSchema,
  lines: z.array(z.strictObject({ lineNo: z.number().int(), quantity: DecimalStringSchema })),
  reason: z.string(),
  document: z
    .strictObject({
      type: z.enum(DELIVERY_RETURN_DOCUMENTS),
      id: RecordIdSchema,
      number: z.string(),
    })
    .optional(),
  recordedByAccountId: z.string(),
  recordedAt: InstantSchema,
});
export type DeliveryReturn = z.infer<typeof DeliveryReturnSchema>;

export const DeliverySchema = z.strictObject({
  deliveryId: RecordIdSchema,
  number: z.string(),
  state: DeliveryStateSchema,
  source: z.strictObject({
    type: DeliverySourceTypeSchema,
    id: RecordIdSchema,
    number: z.string(),
  }),
  /** A delivery planned for what an earlier one left outstanding. */
  followsDelivery: z.strictObject({ deliveryId: RecordIdSchema, number: z.string() }).optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  fromWarehouseId: RecordIdSchema.optional(),
  toWarehouseId: RecordIdSchema.optional(),
  destination: z.string().optional(),
  scheduledOn: BusinessDateSchema,
  vehicle: VehicleSchema.optional(),
  driver: DriverSchema,
  /** What this delivery carries (for the driver and the receiver; not a ledger). */
  lines: z.array(DeliveryLineSchema),
  attempts: z.number().int().positive(),
  dispatchedAt: InstantSchema.optional(),
  deliveredAt: InstantSchema.optional(),
  receivedBy: z.strictObject({ name: z.string(), accountId: z.string().optional() }).optional(),
  proofNote: z.string().optional(),
  /** Why part of it was damaged, refused or short. */
  exceptionNote: z.string().optional(),
  failure: z.strictObject({ reason: z.string(), at: InstantSchema }).optional(),
  cancellation: z
    .strictObject({ reason: z.string(), at: InstantSchema, afterDispatch: z.boolean() })
    .optional(),
  costs: z.array(DeliveryCostSchema),
  returns: z.array(DeliveryReturnSchema),
  history: z.array(
    z.strictObject({
      state: DeliveryStateSchema,
      at: InstantSchema,
      byAccountId: z.string(),
      note: z.string().optional(),
    }),
  ),
  /** Planned or dispatched and its date has passed. */
  late: z.boolean(),
  /** What the stock records say about the source, so the screen can say what is still needed. */
  sourceState: z.string(),
  note: z.string().optional(),
  recordedByAccountId: z.string(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Delivery = z.infer<typeof DeliverySchema>;

export const CreateDeliverySchema = z.strictObject({
  source: z.strictObject({ type: DeliverySourceTypeSchema, id: RecordIdSchema }),
  /**
   * Part of the source: quantities per source line. Absent: everything still outstanding (the whole
   * source on a first delivery). Never more than is outstanding.
   */
  lines: z
    .array(
      z.strictObject({
        sourceLineNo: z.number().int().positive(),
        quantity: DecimalStringSchema,
      }),
    )
    .min(1)
    .max(200)
    .optional(),
  /** The delivery whose shortfall this one carries (same source). */
  followsDeliveryId: RecordIdSchema.optional(),
  scheduledOn: BusinessDateSchema,
  destination: z.string().trim().min(2).max(300).optional(),
  vehicle: VehicleSchema.optional(),
  driver: DriverSchema,
  note: z.string().trim().max(500).optional(),
  idempotencyKey: z.string().trim().min(8).max(200),
});
export type CreateDelivery = z.infer<typeof CreateDeliverySchema>;

export const DeliveryActionSchema = z
  .strictObject({
    action: z.enum(DELIVERY_ACTIONS),
    /** Deliver: who took it, with a note (and, as a document, the signed paper or photo). */
    receivedBy: z
      .strictObject({
        name: z.string().trim().min(2).max(160),
        accountId: z.string().min(1).max(200).optional(),
      })
      .optional(),
    proofNote: z.string().trim().min(3).max(500).optional(),
    /**
     * Deliver: per line, what the receiver accepted, found damaged and refused. Absent: all accepted.
     * The rest of a line is short. Anything not accepted needs a `reason`.
     */
    lines: z
      .array(
        z.strictObject({
          lineNo: z.number().int().positive(),
          accepted: DecimalStringSchema,
          damaged: DecimalStringSchema.optional(),
          rejected: DecimalStringSchema.optional(),
        }),
      )
      .min(1)
      .max(200)
      .optional(),
    /** Fail and cancel: why. Deliver with exceptions: what happened. */
    reason: z.string().trim().min(3).max(500).optional(),
    /** Replan: the new date. */
    scheduledOn: BusinessDateSchema.optional(),
    expectedVersion: z.number().int().positive(),
  })
  .refine((value) => value.action !== 'deliver' || !!value.receivedBy, {
    message: 'RECEIVER_REQUIRED',
    path: ['receivedBy'],
  })
  .refine((value) => !['fail', 'cancel'].includes(value.action) || !!value.reason, {
    message: 'REASON_REQUIRED',
    path: ['reason'],
  })
  .refine((value) => value.action !== 'replan' || !!value.scheduledOn, {
    message: 'SCHEDULE_REQUIRED',
    path: ['scheduledOn'],
  });
export type DeliveryAction = z.infer<typeof DeliveryActionSchema>;

export const AddDeliveryCostSchema = z
  .strictObject({
    kind: z.enum(DELIVERY_COST_KINDS),
    amount: MoneySchema,
    treatment: z.enum(DELIVERY_COST_TREATMENTS),
    /** `companyExpense`: the expense record that carries and posts it. */
    expenseId: RecordIdSchema.optional(),
    /**
     * `onSupplierInvoice`: the purchase order's charge line this cost allocates (optional when the
     * order has exactly one line of that kind). The stable link that stops freight counting twice.
     */
    supplierChargeLineNo: z.number().int().positive().optional(),
    /**
     * A separate charge's own reference — the carrier's invoice or receipt number. Required for
     * company-paid freight on an order whose supplier already charges freight; charged once per source.
     */
    reference: z.string().trim().min(2).max(80).optional(),
    note: z.string().trim().min(3).max(300).optional(),
    /** A retry with the same key records nothing new. */
    idempotencyKey: z.string().trim().min(8).max(200).optional(),
    expectedVersion: z.number().int().positive(),
  })
  .refine((value) => value.treatment !== 'companyExpense' || !!value.expenseId, {
    message: 'EXPENSE_REQUIRED',
    path: ['expenseId'],
  });
export type AddDeliveryCost = z.infer<typeof AddDeliveryCostSchema>;

/** Withdraw a cost recorded in error; it stays on the delivery with the reason and stops counting. */
export const WithdrawDeliveryCostSchema = z.strictObject({
  reason: z.string().trim().min(3).max(300),
  expectedVersion: z.number().int().positive(),
});
export type WithdrawDeliveryCost = z.infer<typeof WithdrawDeliveryCostSchema>;

/**
 * A purchase order's supplier charge lines and how deliveries share them: what was charged, invoiced
 * and credited by the supplier, what deliveries allocated, and what is still unallocated.
 */
export const SupplierChargeSummarySchema = z.strictObject({
  orderId: RecordIdSchema,
  number: z.string(),
  charges: z.array(
    z.strictObject({
      lineNo: z.number().int().positive(),
      kind: z.enum(['freight', 'other']),
      amount: MoneySchema,
      invoiced: MoneySchema,
      credited: MoneySchema,
      allocated: MoneySchema,
      unallocated: MoneySchema,
      allocations: z.array(
        z.strictObject({
          deliveryId: RecordIdSchema,
          number: z.string(),
          costId: RecordIdSchema,
          amount: MoneySchema,
        }),
      ),
    }),
  ),
});
export type SupplierChargeSummary = z.infer<typeof SupplierChargeSummarySchema>;

export const RecordDeliveryReturnSchema = z.strictObject({
  lines: z
    .array(z.strictObject({ lineNo: z.number().int().positive(), quantity: DecimalStringSchema }))
    .min(1)
    .max(200),
  reason: z.string().trim().min(3).max(500),
  /** The stock document (a return, a supplier return) or goods receipt that records the goods. */
  document: z
    .strictObject({ type: z.enum(DELIVERY_RETURN_DOCUMENTS), id: RecordIdSchema })
    .optional(),
  expectedVersion: z.number().int().positive(),
});
export type RecordDeliveryReturn = z.infer<typeof RecordDeliveryReturnSchema>;

export const StockDeliveryQuerySchema = z.strictObject({
  state: DeliveryStateSchema.optional(),
  sourceType: DeliverySourceTypeSchema.optional(),
  sourceId: RecordIdSchema.optional(),
  late: z.enum(['true', 'false']).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const DeliveryOutstandingQuerySchema = z.strictObject({
  sourceType: DeliverySourceTypeSchema,
  sourceId: RecordIdSchema,
});

/**
 * Where a source stands across all its deliveries, per line: planned or on the road, accepted (net
 * of returns), damaged, refused, and still outstanding. For a transfer, `stockReceived` is what the
 * receiving keeper acknowledged — the only quantity that entered the store.
 */
export const DeliveryOutstandingSchema = z.strictObject({
  source: z.strictObject({
    type: DeliverySourceTypeSchema,
    id: RecordIdSchema,
    number: z.string(),
    state: z.string(),
  }),
  lines: z.array(
    z.strictObject({
      sourceLineNo: z.number().int().positive(),
      description: z.string(),
      unit: z.string(),
      quantity: DecimalStringSchema,
      inFlight: DecimalStringSchema,
      accepted: DecimalStringSchema,
      damaged: DecimalStringSchema,
      rejected: DecimalStringSchema,
      returned: DecimalStringSchema,
      outstanding: DecimalStringSchema,
      stockReceived: DecimalStringSchema.optional(),
    }),
  ),
  deliveries: z.array(
    z.strictObject({ deliveryId: RecordIdSchema, number: z.string(), state: DeliveryStateSchema }),
  ),
});
export type DeliveryOutstanding = z.infer<typeof DeliveryOutstandingSchema>;

export const DELIVERY_AUDIT_ACTIONS = {
  planned: 'stores.delivery.planned',
  dispatched: 'stores.delivery.dispatched',
  delivered: 'stores.delivery.delivered',
  failed: 'stores.delivery.failed',
  replanned: 'stores.delivery.replanned',
  cancelled: 'stores.delivery.cancelled',
  costRecorded: 'stores.delivery.costRecorded',
  costWithdrawn: 'stores.delivery.costWithdrawn',
  returned: 'stores.delivery.returned',
} as const;
